// @flow

import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { SchemaWithPermission } from "../types/Schema";
import { SchemaTreeWithPermission } from "../types/SchemaTree";
import { SchemaTreeGraph } from "../components/Schemas/SchemaTree/SchemaTreeLogic";
import { GradingService } from "../types/GradingService";
import { TreeViewRowComponent } from "../components/Schemas/SchemaTree/TreeViewRowComponent";
import { StaleListNotice } from "../components/StaleListNotice";
import { ApiContext } from "../context/KeaAPI/newApi";

export const SchemaTreeView = () => {
  const { t } = useTranslation();
  let navigate = useNavigate();
  const { gradingServiceApi, byteFileApi, keyValueApi, schemaApi, schemaTreeApi, textFileApi } = useContext(ApiContext);

  const [dataLoaded, setDataLoaded] = useState(false);
  // A failed load must be distinguishable from an empty list, otherwise the page
  // claims there are no schemas while the backend is simply unreachable.
  const [loadFailed, setLoadFailed] = useState(false);

  // All these data are needed to render the schema tree including components.
  // The data is loaded async and the page is only rendered once all data is loaded.
  useEffect(() => {
    Promise.all([
      byteFileApi.getAll(),
      textFileApi.getAll(),
      keyValueApi.getAll(),
      schemaApi.getAll(),
      schemaTreeApi.getAll(),
      gradingServiceApi.getAll(),
    ]).then((results) => {
      setLoadFailed(results.some((succeeded) => !succeeded));
      setDataLoaded(true);
    });
  }, []);

  // Handle the buttons that don't have a component
  const addRootSchema = () => {
    navigate("/add-schema");
  };

  // items
  const getTreeTable = (
    schemas: SchemaWithPermission[],
    edges: SchemaTreeWithPermission[],
    gradingServices: GradingService[],
  ) => {
    const schemaTree = new SchemaTreeGraph([...schemas, ...gradingServices], edges);
    const orderedList = schemaTree.getOrderedListRepresentation();
    return orderedList.flatMap((it, index) => <TreeViewRowComponent key={index} entry={it} />);
  };

  // Copied/Adapted from AssignmentListPage.tsx
  const getSchemaTable = () => {
    if (!dataLoaded) {
      return <div>Loading...</div>;
    }

    return (
      <div className="table-container">
        <table className="table is-striped is-narrow is-hoverable is-fullwidth">
          <thead className="is-italic">
            <tr>
              <td>{t("name")}</td>
              <td>{t("updated")}</td>
              <td className="is-hidden-mobile">{t("actions")}</td>
            </tr>
          </thead>
          <tbody>{getTreeTable(schemaApi.elements, schemaTreeApi.elements, gradingServiceApi.elements)}</tbody>
        </table>
      </div>
    );
  };

  const getEmptyMessage = () => {
    if (loadFailed) {
      return <div className="notification is-danger is-light">{t("listLoadFailed")}</div>;
    }
    return <p className="is-size-7">{t("noSchema")} </p>;
  };

  // Copied/Adapted from AssignmentListPage.tsx
  return (
    <div className="section">
      <div className="card mt-6">
        <header className="card-header">
          <p className="card-header-title">{t("schemas")}</p>
        </header>

        <div className="card-content">
          <div className="content">
            <button className="button is-info" onClick={() => addRootSchema()}>
              {t("newSchema")}
            </button>
            <div className="card my-2">
              <div className="card-content">
                <div className="content">
                  <div className="columns"></div>
                  <div className="columns">
                    <div className="column has-text-left">
                      <StaleListNotice loadFailed={loadFailed} hasEntries={schemaApi.elements.length > 0} />
                      {schemaApi.elements.length > 0 && getSchemaTable()}
                      {schemaApi.elements.length === 0 && getEmptyMessage()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
